import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { FileText, Plus, Loader2 } from "lucide-react";
import { DocStatusPill } from "@/components/docs/DocShared";

export function TaskDocs({ taskId, apiClient = api, onLeave }) {
  const nav = useNavigate();
  const navigate = (to) => { onLeave?.(); setTimeout(() => nav(to), 0); };
  const [docs, setDocs] = useState(null);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { apiClient.get(`/console/tasks/${taskId}/docs`).then(({ data }) => setDocs(data.docs)).catch(() => setDocs([])); }, [taskId]);
  const create = async () => {
    setBusy(true);
    try { const { data } = await apiClient.post(`/console/tasks/${taskId}/docs`, { name: name.trim() }); setOpen(false); navigate(`/console/docs/${data.id}`); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setBusy(false); }
  };
  return (
    <div data-testid="task-docs-section">
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs font-mono-data uppercase tracking-wider text-[#9FA1A4]">Docs</p>
        <button data-testid="task-add-doc" onClick={() => { setName(""); setOpen(true); }} className="text-xs text-[#723FFF] hover:text-[#5b2fd6] flex items-center gap-1"><Plus className="h-3.5 w-3.5" /> Add doc</button>
      </div>
      {docs === null ? <Loader2 className="h-4 w-4 animate-spin text-[#c4c5cc]" /> : docs.length === 0 ? <p className="text-xs text-[#c4c5cc]">No docs yet.</p> : (
        <div className="space-y-1.5">
          {docs.map((d) => (
            <button key={d.id} data-testid="task-doc-item" onClick={() => navigate(`/console/docs/${d.id}`)} className="w-full flex items-center gap-2.5 rounded-xl border border-[#EEEFEF] bg-white px-3 py-2 text-left hover:border-[#C9B8FF] transition-colors">
              <span className="h-7 w-7 rounded-lg bg-[#EDE7FF] text-[#723FFF] flex items-center justify-center shrink-0"><FileText className="h-3.5 w-3.5" /></span>
              <span className="text-sm text-[#06060C] truncate flex-1">{d.full_name}</span>
              <DocStatusPill status={d.status} />
            </button>
          ))}
        </div>
      )}
      {open && (
        <Dialog open onOpenChange={() => setOpen(false)}>
          <DialogContent className="bg-white border-[#EEEFEF] max-w-md" data-testid="create-doc-dialog">
            <DialogHeader><DialogTitle className="font-display">Create Document</DialogTitle><DialogDescription className="text-[#9FA1A4]">The doc ID is added automatically, e.g. TASK-1 - Name.</DialogDescription></DialogHeader>
            <Input data-testid="create-doc-name" autoFocus value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && name.trim()) create(); }} placeholder="Document name" className="bg-[#F6F6FD] border-[#EEEFEF]" />
            <DialogFooter>
              <Button data-testid="create-doc-cancel" variant="outline" onClick={() => setOpen(false)} className="rounded-full border-[#EEEFEF]">Cancel</Button>
              <Button data-testid="create-doc-submit" disabled={!name.trim() || busy} onClick={create} className="rounded-full bg-[#06060C] text-white hover:bg-[#1c1c24]">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
